import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight, BadgeCheck, Check, CircleHelp, Database, FileCheck2,
  LockKeyhole, ScanLine, ShieldCheck,
} from "lucide-react";
import { PAID_PLANS, PLAN_OPERATIONAL_NOTE } from "../config/planCatalog";
import PublicFooter from "../components/public/PublicFooter";
import PublicHeader from "../components/public/PublicHeader";
import PublicCtaSection from "../components/public/PublicCtaSection";

const CONTACT = "support@cfinsurance.org";

const pageContent = {
  "/product": {
    eyebrow: "Product",
    title: "A controlled operating system for cleaner CRM data.",
    intro: "CleanIQ turns CRM cleanup into a repeatable workflow: scan records, understand the findings, approve corrections, verify write-back, and retain an audit trail.",
    sections: [
      {
        title: "Find data-quality risk",
        body: "Inspect contacts for duplicates, incomplete records, invalid formats, inconsistent values, and provider-specific tag or label problems without changing the CRM during the scan.",
        points: ["Provider-aware pagination", "Resumable scan checkpoints", "Data-quality findings with supporting context"],
      },
      {
        title: "Review before changing anything",
        body: "Correction cards explain the issue and proposed action. Authorized users can approve individual records or supported batches; protected deletions require explicit confirmation.",
        points: ["Approval-gated actions", "Organization and role checks", "Stale-data validation before write-back"],
      },
      {
        title: "Prove what happened",
        body: "CleanIQ verifies supported provider writes, records audit events, and keeps scan, approval, action, and rollback history separate from the source CRM.",
        points: ["Post-write verification", "Supported rollback controls", "Searchable operational history"],
      },
    ],
  },
  "/connectors": {
    eyebrow: "Supported CRM connectors",
    title: "Connect the CRM you already operate.",
    intro: "CleanIQ supports customer-managed credentials for three validated CRM providers. Each connector is isolated behind the same safety contract while retaining provider-specific pagination, rate-limit, field, and write-back behavior.",
    connectors: [
      {
        name: "GoHighLevel",
        status: "Available",
        auth: "API key and location credentials",
        detail: "Contact scanning, approved corrections, tags, write verification, rollback, resumable pagination, and provider quota telemetry.",
      },
      {
        name: "HubSpot",
        status: "Available",
        auth: "Private-app token or supported Service Key",
        detail: "Contact scanning, approved write-back, verification, rollback, resumable pagination, cancellation, and quota telemetry.",
      },
      {
        name: "Pipedrive",
        status: "Available",
        auth: "Customer API token",
        detail: "Person scanning, approved field and label operations, verification, rollback, cursor pagination, cancellation, and quota telemetry.",
      },
    ],
  },
  "/use-cases": {
    eyebrow: "Use cases",
    title: "Make CRM quality part of normal operations.",
    intro: "CleanIQ helps teams replace occasional spreadsheet cleanup with a visible, controlled process tied to the CRM they already use.",
    sections: [
      {
        title: "Agency and client operations",
        body: "Review duplicate outreach risk, missing contact details, inconsistent assignments, and tag or label structure across connected agency workspaces.",
        points: ["Separate CRM connections by organization", "Plan-based seats and connection limits", "Team-visible scan and approval history"],
      },
      {
        title: "Revenue operations",
        body: "Find records that weaken segmentation, routing, reporting, and follow-up before those errors spread through automation.",
        points: ["Prioritized findings", "Bulk review where supported", "Health trends and remaining issues"],
      },
      {
        title: "CRM migration and maintenance",
        body: "Establish a cleaner baseline before a migration or major workflow change, then run recurring scans to catch new decay.",
        points: ["Read-only discovery first", "Controlled correction decisions", "CRM remains the system of record"],
      },
    ],
  },
  "/pricing": {
    eyebrow: "Plans and pricing",
    title: "Choose capacity that matches your CRM operation.",
    intro: "Plans scale by active contacts, CRM connections, CleanIQ users, and processing priority. The same approval and security boundaries apply at every paid tier.",
  },
  "/faq": {
    eyebrow: "Frequently asked questions",
    title: "Straight answers about how CleanIQ works.",
    intro: "The important boundaries—what CleanIQ connects to, what it stores, and when it changes a CRM—should be clear before you create an account.",
    faqs: [
      ["Does CleanIQ replace my CRM?", "No. Your connected CRM remains the authoritative system of record. CleanIQ analyzes records, stores limited operational metadata and generated findings, and performs only supported actions you authorize."],
      ["Does a scan change my CRM?", "No. Scanning is read-only. A supported correction is written only after the required approval and authorization checks, unless an authorized customer deliberately enables an available auto-execute rule."],
      ["Which CRMs are supported?", "GoHighLevel, HubSpot, and Pipedrive are available. CleanIQ does not advertise a generic connector for unknown CRM systems."],
      ["How are CRM credentials handled?", "Credentials are encrypted server-side, scoped to the owning organization, redacted from logs and responses, and never placed in browser storage or source control."],
      ["Can one organization connect multiple CRMs?", "Connection limits follow the selected plan: Starter supports 1, Growth supports up to 3, and Pro supports up to 10. An organization can assign its team members to the connections they should access."],
      ["What does unlimited cleaning mean?", `It means unlimited cleaning jobs within operational safeguards. ${PLAN_OPERATIONAL_NOTE}`],
      ["Does CleanIQ guarantee every issue will be detected?", "No. Findings depend on source-data quality, field availability, provider behavior, and the supported rules. CleanIQ presents evidence and confidence so users can review the result."],
      ["Does Calia act without approval?", "Calia can explain findings and prepare supported proposals. Actions still pass through CleanIQ authorization and approval controls; high-risk or unsupported actions remain unavailable."],
    ],
  },
  "/contact": {
    eyebrow: "Contact and sales",
    title: "Talk with the people operating CleanIQ.",
    intro: "Ask about plan fit, supported CRM connections, security review, billing, or a workflow you want to validate before creating an account.",
    sections: [
      {
        title: "Product and sales",
        body: "Describe your CRM provider, approximate active-contact count, number of team members, and the cleanup outcome you need.",
        points: ["Plan and connection fit", "Supported-provider questions", "Pre-purchase workflow review"],
      },
      {
        title: "Privacy, security, and legal",
        body: "Use the same monitored contact for privacy requests, security reports, legal notices, or questions about the Trust Center.",
        points: ["Account-linked support remains available after sign-in", "Do not send passwords or CRM API keys by email", "Security issues receive incident triage"],
      },
    ],
  },
  "/resources": {
    eyebrow: "CRM data-quality resources",
    title: "A practical glossary for cleaner CRM operations.",
    intro: "Use these definitions to align the people reviewing findings, approving corrections, and measuring improvement.",
    glossary: [
      ["CRM health score", "A summarized view of detected data-quality conditions. It is an operational indicator, not a guarantee that every record is correct."],
      ["Finding", "A detected condition—such as a likely duplicate, incomplete field, invalid format, or inconsistent value—that may require review."],
      ["Confidence", "The strength of the available evidence supporting a finding or proposed correction."],
      ["Approval gate", "The authorization step that separates a recommendation from a CRM write-back."],
      ["Write-back", "A supported change sent to the connected CRM after required approval and validation."],
      ["Rollback", "A supported compensating action that restores the verified prior value after an approved write."],
      ["System of record", "The authoritative CRM where source customer records remain. CleanIQ is not a replacement CRM or source-record backup."],
      ["Operational metadata", "Limited identifiers, timestamps, findings, approvals, action state, audit history, and errors retained to operate and secure CleanIQ."],
      ["Resumable scan", "A scan that checkpoints provider pagination so interrupted work can continue without treating the entire CRM as one fragile request."],
      ["Provider quota", "The CRM provider’s API budget or burst limit. CleanIQ records available telemetry and uses bounded retry behavior."],
    ],
  },
};

function PageHero({ page }) {
  return <section className="border-b border-slate-200 bg-[#f8fafc] px-6 py-20 lg:px-8">
    <div className="mx-auto max-w-5xl">
      <p className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-[#c85d1e]">{page.eyebrow}</p>
      <h1 className="mt-5 max-w-4xl font-heading text-4xl font-extrabold tracking-[-0.035em] text-slate-950 sm:text-6xl">{page.title}</h1>
      <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600">{page.intro}</p>
    </div>
  </section>;
}

function StandardSections({ sections }) {
  return <section className="px-6 py-16 lg:px-8">
    <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-3">
      {sections.map((section, index) => <article key={section.title} className="border border-slate-200 bg-white p-7">
        <span className="font-mono text-xs text-slate-400">0{index + 1}</span>
        <h2 className="mt-8 font-heading text-2xl font-bold text-slate-950">{section.title}</h2>
        <p className="mt-4 text-sm leading-7 text-slate-600">{section.body}</p>
        <ul className="mt-6 space-y-3 border-t border-slate-200 pt-5">
          {section.points.map((point) => <li key={point} className="flex gap-3 text-sm leading-6 text-slate-700"><Check className="mt-1 h-4 w-4 shrink-0 text-[#c85d1e]" />{point}</li>)}
        </ul>
      </article>)}
    </div>
  </section>;
}

function ConnectorGrid({ connectors }) {
  return <section className="px-6 py-16 lg:px-8"><div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-3">
    {connectors.map((connector) => <article key={connector.name} className="border border-slate-200 bg-white p-7">
      <div className="flex items-start justify-between gap-4"><Database className="h-7 w-7 text-[#c85d1e]" /><span className="border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800">{connector.status}</span></div>
      <h2 className="mt-8 font-heading text-2xl font-bold text-slate-950">{connector.name}</h2>
      <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{connector.auth}</p>
      <p className="mt-5 text-sm leading-7 text-slate-600">{connector.detail}</p>
    </article>)}
  </div></section>;
}

function PricingGrid() {
  return <section className="px-6 py-16 lg:px-8"><div className="mx-auto max-w-7xl">
    <div className="grid gap-5 lg:grid-cols-3">
      {PAID_PLANS.map((plan) => <article key={plan.id} className={"flex flex-col border p-7 " + (plan.popular ? "border-[#0b1327] bg-[#0b1327] text-white" : "border-slate-200 bg-white")}>
        <p className="font-heading text-2xl font-bold">{plan.name}</p>
        <p className={"mt-2 text-sm " + (plan.popular ? "text-slate-300" : "text-slate-500")}>{plan.audience}</p>
        <p className="mt-7 font-heading text-5xl font-extrabold">{plan.price}<span className="ml-2 text-sm font-normal">/ month</span></p>
        <p className="mt-6 text-sm font-semibold">Up to {plan.contacts} active contacts</p>
        <ul className="mt-7 flex-1 space-y-3">{plan.features.map((feature) => <li key={feature} className="flex gap-3 text-sm leading-6"><Check className="mt-1 h-4 w-4 shrink-0 text-orange-400" />{feature}</li>)}</ul>
        <Link to={`/register?plan=${plan.id}`} className="mt-8 inline-flex items-center justify-center bg-[#e8792e] px-5 py-3 text-sm font-bold text-[#0b1327]">Select {plan.name}</Link>
      </article>)}
    </div>
    <p className="mt-5 text-xs leading-6 text-slate-500">* {PLAN_OPERATIONAL_NOTE}</p>
  </div></section>;
}

function FAQList({ faqs }) {
  return <section className="px-6 py-16 lg:px-8"><div className="mx-auto max-w-4xl divide-y divide-slate-200 border-y border-slate-200">
    {faqs.map(([question, answer]) => <details key={question} className="group py-6">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-5 font-heading text-xl font-bold text-slate-950"><span>{question}</span><CircleHelp className="h-5 w-5 shrink-0 text-[#c85d1e]" /></summary>
      <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">{answer}</p>
    </details>)}
  </div></section>;
}

function Glossary({ glossary }) {
  return <section className="px-6 py-16 lg:px-8"><dl className="mx-auto grid max-w-6xl gap-px bg-slate-200 border border-slate-200 md:grid-cols-2">
    {glossary.map(([term, definition]) => <div key={term} className="bg-white p-7"><dt className="font-heading text-xl font-bold text-slate-950">{term}</dt><dd className="mt-3 text-sm leading-7 text-slate-600">{definition}</dd></div>)}
  </dl></section>;
}

function CTA() {
  return <PublicCtaSection className="px-6 py-16 text-white lg:px-8"><div className="mx-auto flex max-w-6xl flex-col justify-between gap-8 lg:flex-row lg:items-center">
    <div><p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-orange-300">Start with visibility</p><h2 className="mt-4 max-w-2xl font-heading text-3xl font-extrabold">See the quality issues before deciding what changes.</h2></div>
    <div className="flex flex-wrap gap-3"><Link to="/register" className="inline-flex items-center bg-[#e8792e] px-6 py-3 text-sm font-bold text-[#0b1327]">Create workspace <ArrowRight className="ml-2 h-4 w-4" /></Link><Link to="/contact" className="inline-flex items-center border border-white/30 px-6 py-3 text-sm font-bold">Contact CleanIQ</Link></div>
  </div></PublicCtaSection>;
}

export default function PublicContent() {
  const pathname = useLocation().pathname;
  const page = pageContent[pathname] || pageContent["/product"];

  return <div className="min-h-screen bg-white text-slate-800">
    <PublicHeader />
    <main>
      <PageHero page={page} />
      {page.sections && <StandardSections sections={page.sections} />}
      {page.connectors && <ConnectorGrid connectors={page.connectors} />}
      {pathname === "/pricing" && <PricingGrid />}
      {page.faqs && <FAQList faqs={page.faqs} />}
      {page.glossary && <Glossary glossary={page.glossary} />}
      {pathname === "/connectors" && <section className="px-6 pb-16 lg:px-8"><div className="mx-auto max-w-6xl border border-blue-200 bg-blue-50 p-6"><div className="flex gap-4"><ShieldCheck className="h-6 w-6 shrink-0 text-blue-700" /><div><h2 className="font-heading text-xl font-bold text-slate-950">Need another CRM?</h2><p className="mt-2 text-sm leading-7 text-slate-700">Tell us which provider you use. CleanIQ evaluates a connector only after its authentication, pagination, field mapping, rate limits, scan behavior, and safe write-back contract are defined.</p><Link to="/contact" className="mt-4 inline-flex min-h-11 min-w-11 items-center text-sm font-bold text-blue-800">Request a CRM review <ArrowRight className="ml-2 h-4 w-4" /></Link></div></div></div></section>}
      {pathname === "/contact" && <section className="px-6 pb-16 lg:px-8"><div className="mx-auto max-w-6xl bg-[#eef2f7] p-8"><div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-center"><div><p className="font-heading text-2xl font-bold text-slate-950">Email CleanIQ</p><p className="mt-2 text-sm text-slate-600">Never email passwords, access tokens, or CRM API keys.</p></div><a href={`mailto:${CONTACT}`} className="inline-flex min-w-0 items-center justify-center break-all bg-[#e8792e] px-6 py-3 text-sm font-bold text-[#0b1327]">{CONTACT}</a></div></div></section>}
      {pathname === "/product" && <section className="border-t border-slate-200 bg-[#f8fafc] px-6 py-14 lg:px-8"><div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-3">{[[ScanLine,"Read-only scan"],[FileCheck2,"Approval-gated action"],[LockKeyhole,"Protected credentials"]].map(([Icon,label])=><div key={label} className="flex items-center gap-3 font-semibold text-slate-800"><Icon className="h-5 w-5 text-[#c85d1e]" />{label}</div>)}</div></section>}
      {pathname === "/resources" && <section className="px-6 pb-16 lg:px-8"><div className="mx-auto flex max-w-6xl gap-4 border border-amber-200 bg-amber-50 p-6"><BadgeCheck className="h-6 w-6 shrink-0 text-amber-700" /><p className="text-sm leading-7 text-slate-700">These definitions describe CleanIQ’s current product language. Provider capabilities and plan limits remain governed by the live product and applicable order.</p></div></section>}
      <CTA />
    </main>
    <PublicFooter />
  </div>;
}
